import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingBreed from '../../interfaces/IMappingBreed'
import { SearchRequest, MappingBreedSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingBreedList, mappingBreedSave } from '../../services/api'

interface MappingBreedContextState extends BaseContextState {
  mappingBreeds: Array<IMappingBreed> | null,
  mappingBreed: IMappingBreed | null
}

interface MappingBreedContextData extends MappingBreedContextState {
  resetErrors: Function,
  requestMappingBreedList: (args: SearchRequest) => Promise<any>,
  requestMappingBreedFind: (args: SearchRequest) => Promise<any>,
  requestMappingBreedSave: (args: MappingBreedSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingBreedContextData)

// Export consumer
export const MappingBreedConsumer = Consumer

export class MappingBreedProvider extends BaseContextProvider {
  state: MappingBreedContextState = {
    mappingBreeds: null,
    mappingBreed: null
  }

  /**
   * Make api request for mapping breed list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingBreedList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingBreedList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingBreeds'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping age finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingBreedFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingBreed: null
      }, async () => {
        payload.search = {
          ...payload.search,
          specie: {
            with: true
          },
          breed: {
            with: true
          },
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"]
            }
          }
        }
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingBreedList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingBreed'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping breed save
   * 
   * @param {MappingBreedSaveRequest} payload 
   */
  requestMappingBreedSave = (payload: MappingBreedSaveRequest) => {
    // No need to null `mappingBreed` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingBreed: null
    }, async () => {
      try {
        const response = await mappingBreedSave(payload)

        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_breed', destination: 'mappingBreed' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingBreedList: this.requestMappingBreedList,
          requestMappingBreedFind: this.requestMappingBreedFind,
          requestMappingBreedSave: this.requestMappingBreedSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
